import { api } from './api';

/** Push needs a service worker, which needs HTTPS (or localhost). */
export const pushSupported =
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window &&
  'Notification' in window;

/** « Chrome sur Android » — enough to recognise a device in the Réglages list. */
export function deviceLabel(userAgent = navigator.userAgent): string {
  const browser =
    /Edg\//.test(userAgent) ? 'Edge'
    : /OPR\//.test(userAgent) ? 'Opera'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Safari\//.test(userAgent) ? 'Safari'
    : 'Navigateur';

  const system =
    /Android/.test(userAgent) ? 'Android'
    : /iPhone|iPad|iPod/.test(userAgent) ? 'iOS'
    : /Windows/.test(userAgent) ? 'Windows'
    : /Mac OS X/.test(userAgent) ? 'macOS'
    : /Linux/.test(userAgent) ? 'Linux'
    : 'cet appareil';

  return `${browser} sur ${system}`;
}

export async function currentEndpoint(): Promise<string | null> {
  if (!pushSupported) return null;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return subscription?.endpoint ?? null;
}

/**
 * Asks for the notification permission, subscribes this browser to the server's
 * VAPID key and registers the result. Throws a French message on every refusal.
 */
export async function enablePush(): Promise<void> {
  if (!pushSupported) {
    throw new Error('Cet appareil ne gère pas les notifications.');
  }

  const { publicKey } = await api.push.publicKey();
  if (!publicKey) {
    throw new Error('Le serveur n’a pas de clés de notification (VAPID) configurées.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notifications refusées par le navigateur.');
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeBase64Url(publicKey),
    }));

  const json = subscription.toJSON();
  if (!json.keys?.p256dh || !json.keys.auth) {
    throw new Error('Abonnement incomplet : réessayez.');
  }

  await api.push.subscribe({
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    deviceLabel: deviceLabel(),
  });
}

/** Unsubscribes this browser and removes its server-side record. */
export async function disablePush(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  const devices = await api.push.devices();
  const registered = devices.find((device) => device.endpoint === subscription.endpoint);
  await subscription.unsubscribe();
  if (registered) await api.push.removeDevice(registered.id);
}

/** The VAPID key travels as base64url; `subscribe` wants the raw bytes. */
function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
